// components/blog/SupportPopup.jsx
// Appears once per session after 70% scroll or 30 seconds.
// Accepts an optional `onLike` callback  -  when provided, the dismiss
// "I appreciate the writing" button also likes the current article and
// plays a little heart sound.
import { useEffect, useRef, useState } from "react";
import { X, Coffee, Heart, ExternalLink } from "lucide-react";
import { playPop, playLike }               from "../../lib/audioUtils";
import { msUntilNextPopup, recordPopupShown } from "../../lib/popupUtils";

const SESSION_KEY = "mc_support_popup_shown";
const KOFI_URL    = "https://ko-fi.com/malickecase";
const BASE_DELAY  = 30_000;

const QUIPS = [
  { msg: "Still here? Your attention span is seriously impressive. Most people left after the intro. 😏", cta: "Buy me a coffee ☕" },
  { msg: "You've read this far, which means you liked it, right? RIGHT?? Asking for a friend. 👀", cta: "Support the writing" },
  { msg: "Fun fact: writers need coffee. Lots of it. You could help with that. Just saying. ☕", cta: "Feed the writer" },
  { msg: "You're in the top 3% of readers who actually finish articles. Wild. Also: hi. 👋", cta: "Say thanks with coffee" },
  { msg: "I wrote this for free. You read it for free. The coffee is NOT free though. JK it's voluntary. 😅", cta: "Keep me caffeinated" },
  { msg: "No algorithm. No ads. Just vibes and caffeine. If you enjoyed it, maybe top up the caffeine? ☕", cta: "Buy me a coffee" },
  { msg: "This whole site runs on 3 things: curiosity, bad ideas, and coffee. Two of those are free. 👀", cta: "Fund the coffee habit" },
  { msg: "You actually read the whole thing. I'm genuinely shocked. My mum doesn't even do that. 😂", cta: "Treat the writer" },
  { msg: "Plot twist: the writer is still alive and would appreciate a coffee. Just a plot twist. ☕", cta: "Plot twist accepted" },
  { msg: "If this post saved you one Google search, it already paid for itself. Coffee is the tip. 😄", cta: "Leave a tip ☕" },
];

const LOCKED_QUIPS = [
  { msg: "Peeking at a locked post? Bold move. The writing's good, I promise. A coffee in solidarity? ☕", cta: "Support anyway" },
  { msg: "Can't read it without an account, but it still costs coffee to write. Just saying. 😅", cta: "Buy me a coffee ☕" },
  { msg: "A paywall stood between you and a good read. The least I can do is offer you solidarity vibes. ☕", cta: "Support the creator" },
  { msg: "Locked content, open donation page. Very democratic. You decide if I eat today. 👀", cta: "Feed the writer ☕" },
];

const STYLES = `
@keyframes spSlideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(28px) scale(0.95); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1);    }
}
@keyframes spSlideDown {
  from { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1);    }
  to   { opacity: 0; transform: translateX(-50%) translateY(28px) scale(0.95); }
}
.sp-enter { animation: spSlideUp   320ms cubic-bezier(0.34,1.56,0.64,1) forwards; }
.sp-exit  { animation: spSlideDown 220ms ease forwards; }
@keyframes coffeeSteam {
  0%,100% { opacity: 0.15; transform: translateY(0)    scaleX(1);    }
  50%     { opacity: 0.55; transform: translateY(-6px) scaleX(1.15); }
}
.sp-steam             { animation: coffeeSteam 2s ease-in-out infinite; }
.sp-steam:nth-child(2){ animation-delay: 0.4s; }
.sp-steam:nth-child(3){ animation-delay: 0.8s; }
@keyframes coffeeWobble {
  0%,100% { transform: rotate(-3deg); }
  50%     { transform: rotate(3deg);  }
}
.sp-cup { animation: coffeeWobble 2.8s ease-in-out infinite; display: inline-block; }
@keyframes spPulse {
  0%,100% { transform: scale(1);   }
  50%     { transform: scale(1.06); }
}
.sp-cta-pulse { animation: spPulse 2s ease-in-out infinite; }
`;

export function SupportPopup({ isDark, isMembersLocked = false, onLike }) {
  const [phase, setPhase]  = useState("hidden");
  const pool               = isMembersLocked ? LOCKED_QUIPS : QUIPS;
  const quip               = useRef(pool[Math.floor(Math.random() * pool.length)]);
  const triggered          = useRef(false);
  const timerRef           = useRef(null);
  const [redirectWarn, setRedirectWarn] = useState(false);

  function show() {
    recordPopupShown();
    playPop();
    setPhase("entering");
    setTimeout(() => setPhase("visible"), 30);
  }

  function trigger() {
    if (triggered.current) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    triggered.current = true;
    sessionStorage.setItem(SESSION_KEY, "1");
    const gap = msUntilNextPopup();
    if (gap > 0) setTimeout(show, gap + 500);
    else show();
  }

  function dismiss() {
    setPhase("exiting");
    setTimeout(() => setPhase("hidden"), 240);
  }

  function handleAppreciate() {
    // Like the post (if we're on an accessible article) then dismiss.
    if (!isMembersLocked && onLike) {
      onLike();
      playLike();
    }
    dismiss();
  }

  function confirmRedirect() {
    window.open(KOFI_URL, "_blank", "noopener,noreferrer");
    setRedirectWarn(false);
    dismiss();
  }

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const delay = BASE_DELAY + msUntilNextPopup();
    timerRef.current = setTimeout(trigger, delay);

    function onScroll() {
      if (isMembersLocked) return;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total > 0 && window.scrollY / total >= 0.70) {
        clearTimeout(timerRef.current);
        trigger();
        window.removeEventListener("scroll", onScroll);
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(timerRef.current); window.removeEventListener("scroll", onScroll); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "hidden") return null;

  const bg  = isDark ? "#111114" : "#EDEBE5";
  const bdr = isDark ? "#27272F" : "#C8C4BC";

  return (
    <>
      <style>{STYLES}</style>

      {redirectWarn && (
        <div onClick={() => setRedirectWarn(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backdropFilter: "blur(4px)" }}>
          <div className="blog-theme" data-blog-theme={isDark ? "dark" : "light"} onClick={e => e.stopPropagation()}
            style={{ background: bg, border: `0.5px solid ${bdr}`, borderRadius: 12, padding: "24px 28px", maxWidth: 360, width: "100%", textAlign: "center", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <ExternalLink style={{ width: 28, height: 28, margin: "0 auto 12px", color: "var(--blog-accent-teal)" }} />
            <div className="font-display font-medium" style={{ fontSize: 17, color: "var(--blog-text-primary)", marginBottom: 8 }}>Heading to Ko-fi</div>
            <p style={{ fontSize: 12, color: "var(--blog-text-secondary)", lineHeight: 1.6, marginBottom: 20 }}>
              Opens <strong>ko-fi.com</strong> in a new tab. Safe, trusted, no monthly fee.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={confirmRedirect} style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>Take me there ☕</button>
              <button onClick={() => setRedirectWarn(false)} style={{ padding: "10px 18px", borderRadius: 8, border: "0.5px solid var(--blog-border-primary)", background: "transparent", color: "var(--blog-text-secondary)", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className={`blog-theme ${phase === "exiting" ? "sp-exit" : "sp-enter"}`} data-blog-theme={isDark ? "dark" : "light"}
        style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", zIndex: 9700, width: "min(440px, calc(100vw - 32px))", background: bg, border: `0.5px solid ${bdr}`, borderRadius: 14, boxShadow: isDark ? "0 24px 60px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.5)" : "0 24px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>
        <div style={{ height: 3, background: "linear-gradient(90deg, var(--blog-accent-amber), #f59e0b)" }} />
        <div style={{ padding: "18px 20px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flexShrink: 0, userSelect: "none" }}>
              <div style={{ display: "flex", gap: 4, justifyContent: "center", height: 14, marginBottom: 2 }}>
                {[0,1,2].map(i => <div key={i} className="sp-steam" style={{ width: 2, height: 10, borderRadius: 99, background: "var(--blog-accent-amber)", opacity: 0.4 }} />)}
              </div>
              <span className="sp-cup" style={{ fontSize: 36, lineHeight: 1 }}>☕</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: "0.12em", color: "var(--blog-accent-amber)", marginBottom: 6 }}>A word from the writer</div>
                <button onClick={dismiss} aria-label="Dismiss" style={{ background: "transparent", border: "none", color: "var(--blog-text-tertiary)", cursor: "pointer", padding: 2, marginTop: -2, lineHeight: 1 }}>
                  <X style={{ width: 14, height: 14 }} />
                </button>
              </div>
              <p className="font-display" style={{ fontSize: 14, lineHeight: 1.5, color: "var(--blog-text-primary)", marginBottom: 14, fontWeight: 500 }}>
                {quip.current.msg}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <button onClick={() => setRedirectWarn(true)} className="sp-cta-pulse"
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 8, border: "none", background: "var(--blog-accent-amber)", color: "#1a1a1a", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                  <Coffee style={{ width: 13, height: 13 }} />
                  {quip.current.cta}
                </button>
                <button onClick={handleAppreciate}
                  style={{ background: "transparent", border: "none", color: "var(--blog-text-tertiary)", fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: "4px 0", display: "flex", alignItems: "center", gap: 4 }}>
                  <Heart style={{ width: 11, height: 11 }} />
                  {isMembersLocked ? "Maybe another time" : "I appreciate the writing"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
